import Styles from './NavMenu.module.css';
import { Button } from '@app/engine';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/** Single item in the nav menu dropdown. */
interface INavMenuItem {
  label: string;
  onClick?: () => void;
  href?: string;
}

/** Props for the NavMenu hamburger dropdown. */
interface INavMenuProps {
  triggerLabel: string;
  items: INavMenuItem[];
  classNameTrigger?: string;
}

/** File-local constants for the nav menu. */
const localConstants = {
  pointerEvent: 'pointerdown'
} as const;

/**
 * Compact hamburger menu that opens a dropdown of navigation items. Closes on outside click. Styled to match the LanguageSelector dropdown.
 *
 * @param props - Component props.
 * @returns The nav menu element.
 */
export function NavMenu(props: INavMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close the menu when clicking outside it.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener(localConstants.pointerEvent, onPointerDown);
    return () => document.removeEventListener(localConstants.pointerEvent, onPointerDown);
  }, [open]);

  const handleClick = useCallback((onClick?: () => void) => {
    onClick?.();
    setOpen(false);
  }, []);

  const triggerClass = props.classNameTrigger ? props.classNameTrigger : Styles.navMenuTrigger;

  return (
    <div ref={rootRef} className={Styles.navMenu}>
      <Button text={props.triggerLabel} type="button" classNameButtonEnabled={triggerClass} onClickHandler={() => setOpen((prev) => !prev)} />
      {open && (
        <div className={Styles.navMenuDropdown} role="menu">
          {props.items.map((item, index) =>
            item.href ? (
              <Link key={index} to={item.href} className={Styles.navMenuOption} onClick={() => setOpen(false)}>
                {item.label}
              </Link>
            ) : (
              <Button key={index} text={item.label} type="button" classNameButtonEnabled={Styles.navMenuOption} onClickHandler={() => handleClick(item.onClick)} />
            )
          )}
        </div>
      )}
    </div>
  );
}
